// src/pages/subscription/AddOnPurchasePage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useSubscription, useSubscriptionAddon, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '--';
  return (amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const AddOnPurchasePage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useSubscriptionAddon();
  const subscriptionStore = useSubscription();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    subscription_id: '',
    quantity: 1,
  });
  const item = store.currentItem as any;

  useEffect(() => {
    if (id) store.GetAddOn(id);
    return () => { store.clearCurrent(); };
  }, [id]);

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setIsSubmitting(true);
    try {
      await subscriptionStore.PurchaseAddOn(formData.subscription_id, {
        addon: { id },
        quantity: formData.quantity,
      } as any);
      navigate(`/subscriptions/${formData.subscription_id}`);
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (store.error && !item) {
    return <><ErrorAlert error={store.error} /><Link to="/subscriptions/addons" className="mt-2 inline-block text-sm underline text-primary mt-2 inline-block text-sm underline text-primary">{t("common.backToList")}</Link></>;
  }

  const unitPrice = item?.price_amount ?? 0;
  const totalPrice = unitPrice * formData.quantity;

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">Purchase Add-On</h1>
        <nav className="text-sm">
          <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/subscriptions/addons" className="text-muted-foreground hover:text-foreground">Add-Ons</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Purchase</span>
        </nav>
      </div>

      {(store.error ?? subscriptionStore.error) ? (
        <ErrorAlert
          error={store.error ?? subscriptionStore.error}
          dismissible
          onDismiss={() => { store.clearError(); subscriptionStore.clearError(); }}
        />
      ) : null}

      {/* Add-On Details Card */}
      {item && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{String(item.name ?? '')}</CardTitle>
                <CardDescription>{String(item.description ?? '')}</CardDescription>
              </div>
              <Badge variant="secondary" size="sm">{formatLabel(item.addon_type || '', 'ADDON_TYPE_')}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Unit Price</p>
                <p className="text-lg font-semibold text-foreground">{formatMoney(item.price_amount)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Currency</p>
                <p className="text-lg font-semibold text-foreground">{String(item.currency ?? '--')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Billing Cycle</p>
                <p className="text-lg font-semibold text-foreground">{formatLabel(item.billing_cycle || '', 'BILLING_CYCLE_')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">One-Time</p>
                <p className="text-lg font-semibold text-foreground">
                  {item.is_one_time ? <Badge variant="primary" size="sm">Yes</Badge> : <Badge variant="secondary" size="sm">No</Badge>}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Purchase Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Purchase Details</CardTitle>
            <CardDescription>Specify the subscription and quantity</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Subscription ID"
              type="text"
              value={formData.subscription_id}
              onChange={(e) => handleChange('subscription_id', e.target.value)}
              placeholder="Enter the subscription ID to add this add-on to"
              required
            />
            <Input
              label="Quantity"
              type="number"
              value={String(formData.quantity)}
              onChange={(e) => handleChange('quantity', Math.max(1, parseInt(e.target.value) || 1))}
              placeholder="1"
              required
            />

            {/* Price Summary */}
            <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-2">
              <h3 className="text-sm font-medium text-foreground">Price Summary</h3>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Unit Price</span>
                <span className="text-foreground">{formatMoney(unitPrice)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Quantity</span>
                <span className="text-foreground">{formData.quantity}</span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between text-sm font-semibold">
                <span className="text-foreground">Total</span>
                <span className="text-foreground">{formatMoney(totalPrice)}</span>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to={`/subscriptions/addons/${id}`}><Button type="button" variant="secondary">{t('common.cancel')}</Button></Link>
            <Button type="submit" disabled={isSubmitting || !formData.subscription_id}>
              {isSubmitting ? 'Processing...' : 'Purchase Add-On'}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default AddOnPurchasePage;
